import { Fragment } from "react";

import logoLight from "../../assets/logo-light.svg";
import { VariantTitle } from "../../graphql/inventory/products";
import {
  Customer,
  OrderStatus,
  ProductApprove,
  renderStatusText,
} from "../../graphql/sales/orders";
import { formatDate } from "../../utils";

export type OrderPageProps = {
  customer: Customer;
  orderNumber: string;
  address: string;
  notes: string;
  items: {
    id: string;
    name: string;
    plu?: number | null;
    stockCode: string;
    variantTitle: VariantTitle[];
    price: number;
    quantity: number;
    productApprove: ProductApprove;
  }[];
  orderStatus: OrderStatus;
  orderDate: string;
};
export function OrderPage({ data }: { data: OrderPageProps | null }) {
  if (!data) return null;

  return (
    <div className="px-4 py-6 font-light sm:px-6">
      <div className="pb-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="w-80">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192.9 12.64">
                <path d="M192.87,8.77c0-4-7.4-3.39-7.4-5.26,0-.74.83-1.22,2.09-1.22a5.15,5.15,0,0,1,3.49,1.58l1.85-1.58A6.81,6.81,0,0,0,187.59,0c-3.18,0-5.18,1.71-5.18,3.76C182.41,8,189.75,7,189.75,9c0,.75-.57,1.33-1.92,1.33a6.93,6.93,0,0,1-4.38-1.78l-1.67,1.91a8.91,8.91,0,0,0,5.8,2.16C190.65,12.64,192.87,11,192.87,8.77Zm-15.4-1.12c0,1.46-.92,2.3-2.56,2.3h-2.12V2.68h2.12c1.64,0,2.56.85,2.56,2.31Zm3.2,0V5.06c0-2.63-1.83-4.74-5.58-4.74H169.6v12h5.49C178.84,12.31,180.67,10.28,180.67,7.67Zm-16.33,0a2.49,2.49,0,0,1-5,0V4.9a2.49,2.49,0,0,1,5,0Zm3.24-.25V5.15c0-3-2.22-5.15-5.73-5.15s-5.74,2.16-5.74,5.15V7.47c0,3,2.23,5.17,5.74,5.17S167.58,10.46,167.58,7.47Zm-16.3.25a2.49,2.49,0,0,1-5,0V4.9a2.49,2.49,0,0,1,5,0Zm3.24-.25V5.15c0-3-2.22-5.15-5.73-5.15s-5.74,2.16-5.74,5.15V7.47c0,3,2.23,5.17,5.74,5.17S154.52,10.46,154.52,7.47ZM141.89,2.74V.32h-9.31v12h3.19V7.54h4.07V5.4h-4.07V2.74Zm-16.17,9.57V.32h-3.18V5.2h-4.75V.32H114.6v12h3.19V7.6h4.75v4.71Zm-13-3.54c0-4-7.39-3.39-7.39-5.26,0-.74.82-1.22,2.08-1.22a5.2,5.2,0,0,1,3.5,1.58l1.85-1.58A6.81,6.81,0,0,0,107.42,0c-3.19,0-5.18,1.71-5.18,3.76C102.24,8,109.58,7,109.58,9c0,.75-.58,1.33-1.93,1.33a6.93,6.93,0,0,1-4.37-1.78l-1.67,1.91a8.86,8.86,0,0,0,5.79,2.16C110.48,12.64,112.69,11,112.69,8.77ZM100.5,12.31V9.74H94.21V7.06h4.45V5.24H94.21V2.77h6.29V.32H91v12Zm-15-7.86c0,.84-.5,1.35-1.6,1.35H81.61V2.66H84c1.17,0,1.6.51,1.6,1.28Zm3.8,7.9V12a2.11,2.11,0,0,1-.61-1.62V9a2.31,2.31,0,0,0-2.25-2.44,2.56,2.56,0,0,0,2.25-2.61V3.49C88.74,1.66,87.3.32,84.6.32H78.43v12h3.18V7.56H84c1.19,0,1.53.52,1.55,1.31l0,1.61c0,1.09.39,1.87,1.26,1.87ZM76.74,2.74V.32h-9.3v12h3.18V7.54h4.07V5.4H70.62V2.74ZM56.81,7.81H54l1.46-4.17Zm4.86,4.5L57.12.32H53.91l-4.5,12h3l.87-2.45h4.23l.84,2.45Zm-13.57,0V.32H44.91V5.2H40.16V.32H37v12h3.18V7.6h4.75v4.71ZM32,4.66C32,5.53,31.52,6,30.43,6H28.05V2.7h2.36C31.52,2.7,32,3.19,32,4Zm3.17-.25V3.94C35.16,1.73,33.68.32,31,.32H24.86v12h3.19V8H31C33.68,8,35.16,6.61,35.16,4.41Zm-11.92,7.9V9.76H16.8V.32H13.61v12ZM7.4,7.81H4.54L6,3.64Zm4.86,4.5L7.7.32H4.5L0,12.31H3l.86-2.45H8.06l.85,2.45Z" />
              </svg>
            </div>
            <div className="mt-5 text-lg uppercase">Order Details</div>
          </div>
          <div className="w-1/4 bg-black px-3 py-4">
            <img src={logoLight} alt="logo" />
          </div>
        </div>
      </div>

      <div>
        <div className="w-3/5 bg-gray-300 p-3">
          <div className="grid grid-cols-7 items-center gap-4">
            <div className="col-span-3 uppercase">Order Number</div>
            <div className="col-span-4">
              <div className="bg-[#4BA84F]/50 px-2 py-1.5 text-sm font-normal">
                #{data.orderNumber}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-12 gap-4">
        <div className="col-span-6 space-y-2">
          <div className="flex w-full">
            <div className="uppercase">To:</div>
            <div className="ml-20 font-normal">
              {data.customer.customerName}
            </div>
          </div>
          <div className="flex flex-row text-xs">
            <div className="mr-4">Address:</div>
            <div className="flex-1">{data.address}</div>
          </div>
          <div className="flex flex-row text-xs">
            <div className="mr-4">Order Date:</div>
            <div className="flex-1">
              {formatDate(data.orderDate, "EEEE, dd MMMM yyyy")}
            </div>
          </div>
          <div className="flex flex-row text-sm">
            <div className="mr-4">Notes:</div>
            <div className="flex-1 text-xs">{data.notes}</div>
          </div>
        </div>
        <div className="col-span-6 uppercase">
          <div className="mx-auto w-48 text-right">
            <div className="font-normal">Order Status</div>
            <div className="text-sm">{renderStatusText(data.orderStatus)}</div>
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-1 flex-col justify-between">
        <div className="flex flex-col">
          <table className="min-w-full">
            <thead className="bg-gray-300 text-xs uppercase text-gray-900">
              <tr>
                <th
                  scope="col"
                  className="py-2.5 pl-4 pr-3 text-left font-medium sm:pl-6"
                >
                  #
                </th>
                <th
                  scope="col"
                  className="hidden px-3 py-2.5 text-right font-medium sm:table-cell"
                >
                  Description
                </th>
                <th
                  scope="col"
                  className="hidden px-3 py-2.5 text-right font-medium sm:table-cell"
                >
                  PLU
                </th>
                <th
                  scope="col"
                  className="hidden px-3 py-2.5 text-right font-medium sm:table-cell"
                >
                  Stock Code
                </th>
                <th
                  scope="col"
                  className="hidden px-3 py-2.5 text-right font-medium sm:table-cell"
                >
                  Quantity
                </th>
                <th
                  scope="col"
                  className="py-2.5 pl-3 pr-4 text-right font-medium sm:pr-6"
                >
                  Price $
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={5} className="py-1"></td>
              </tr>
              {data.items.map((item, index) => {
                if (item.productApprove !== ProductApprove.APPROVED)
                  return null;

                return (
                  <Fragment key={item.id}>
                    <tr>
                      <td colSpan={5} className="py-px"></td>
                    </tr>
                    <tr className="border border-gray-200">
                      <td className="py-3 pl-4 pr-3 text-sm text-gray-500 sm:pl-6">
                        {index + 1}
                      </td>
                      <td className="hidden px-3 py-3 text-right text-sm text-gray-500 sm:table-cell">
                        {item.name}
                      </td>
                      <td className="hidden px-3 py-3 text-right text-sm text-gray-500 sm:table-cell">
                        {item.plu}
                      </td>
                      <td className="hidden px-3 py-3 text-right text-sm text-gray-500 sm:table-cell">
                        {item.stockCode}
                      </td>
                      <td className="hidden px-3 py-3 text-right text-sm text-gray-500 sm:table-cell">
                        {item.quantity}
                      </td>
                      <td className="py-3 pl-3 pr-4 text-right text-sm text-gray-500 sm:pr-6">
                        ${(item.price * item.quantity).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={5} className="py-px"></td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
